import React, { useEffect, useState } from "react";
import axios from "axios";
import Grid from "../components/Grid";
import {FaDownload, FaEdit} from "react-icons/fa";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function CashBookCategory() {

    const [categories, setCategories] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [search, setSearch] = useState("");
    const [formData, setFormData] = useState({
        id: "",
        category_name: "",
        type: "Payment",
        description: "",
        status: "Active",
        initial_balance: 0
    });
    const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: 10});
    const [totalRows, setTotalRows] = useState(0);

    const token = sessionStorage.getItem("token");

    useEffect(() => {
        fetchCategories(paginationModel.page, paginationModel.pageSize);
    }, [paginationModel]);

    const fetchCategories = async (page = 0, pageSize = 10) => {

        try {

            const res = await axios.get(
                `${API_BASE_URL}/cashbook-category/get?page=${page + 1}&limit=${pageSize}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setCategories(res.data.data);
            setTotalRows(res.data.totalRecords);

        } catch (err) {
            toast.error("Error loading categories");
        }
    };

    const handleAddClick = () => {
        setIsEdit(false);

        setFormData({
            id:"",
            category_name:"",
            type:"Payment",
            description:"",
            status:"Active",
            initial_balance: 0
        });

        setShowModal(true);
    };

    const handleEdit = (row) => {
        setIsEdit(true);

        setFormData({
            id:row._id,
            category_name:row.category_name,
            type:row.type,
            description:row.description,
            status:row.status
        });

        setShowModal(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData( prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = async () => {
        try{
            if(isEdit){
                await axios.put(`${API_BASE_URL}/cashbook-category/update/${formData.id}`,
                    {
                        description:formData.description,
                        status:formData.status
                    },
                    {
                        headers:{Authorization:`Bearer ${token}`}
                    }
                );
                toast.success("Category updated");
            }else{
                await axios.post(`${API_BASE_URL}/cashbook-category/create`,
                    {
                      category_name: formData.category_name,
                      type:formData.type,
                      description:formData.description,
                      status:formData.status,
                    },
                    {
                        headers:{Authorization:`Bearer ${token}`}
                    }
                    );
                toast.success("Category created");
            }

            setShowModal(false);
            fetchCategories();

        }catch (e) {
            toast.error("Error saving category");
        }
    };

    const columns = [

        { field: "category_code", headerName: "Category Code", flex: 1 },

        { field: "category_name", headerName: "Category Name", flex: 1 },

        { field: "type", headerName: "Type", flex: 1 },

        { field: "description", headerName: "Description", flex: 1 },


        {
            field: "status",
            headerName: "Status",
            flex: 1,
            renderCell: (params) => {
                const color =
                    params.row.status === "Active"
                        ? "bg-green-600"
                        : "bg-red-500";

                return (
                    <span className={`${color} text-white px-2 py-1 rounded`}>
            {params.row.status}
          </span>
                );
            }
        },

        {
            field: "action",
            headerName: "Action",
            flex: 1,
            renderCell: (params) => (

                <div className="flex items-center justify-center w-full h-full gap-2">
                    <button
                        onClick={() => handleEdit(params.row)}
                        className="bg-green-600 hover:bg-green-700 text-white w-8 h-8 rounded flex items-center justify-center">
                        <FaEdit />
                    </button>
                </div>
            )
        }
    ];

    const downloadExcel = async() => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Cashbook Categories");

        worksheet.addRow([
            "Category Code",
            "Category Name",
            "Type",
            "Description",
            "Initial Balance",
            "Status"
        ]);

        categories.forEach(c=> {
            worksheet.addRow([
                c.category_code,
                c.category_name,
                c.type,
                c.description,
                c.status
            ]);
        });

        const buffer = await workbook.xlsx.writeBuffer();

        saveAs(
            new Blob([buffer]),
            "CashBook_Categories.xlsx"
        );
    };

    return (

        <div className="p-6">

            <div className="flex justify-between items-center mb-6">

                <h1 className="text-3xl font-bold text-green-700">
                    CashBook Categories
                </h1>

                <button
                    onClick={handleAddClick}
                    className="bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-800"
                >
                    Add CashBook Category
                </button>

            </div>

            <div className="bg-white p-4 rounded-lg shadow">

                <div className="flex justify-between mb-4">

                    <input
                        type="text"
                        placeholder="Search Category"
                        value={search}
                        onChange={(e)=>setSearch(e.target.value)}
                        className="border px-3 py-2 rounded-lg focus:ring-2 focus:ring-green-500"
                    />

                    <button
                        onClick={downloadExcel}
                        className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                    >
                        <FaDownload className="mr-2"/>
                        Download Excel
                    </button>

                </div>

                <Grid
                    rows={categories}
                    columns={columns}
                    getRowId={(row)=>row._id}
                    rowCount={totalRows}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                />

            </div>

            {showModal && (

                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">

                    <div className="bg-white rounded-xl shadow-2xl w-[520px] overflow-hidden">

                        <div className="bg-green-700 text-white px-6 py-4 flex justify-between items-center">

                            <h2 className="text-lg font-semibold">
                                {isEdit ? "Edit CashBook Category" : "Add CashBook Category"}
                            </h2>

                            <button
                                onClick={()=>setShowModal(false)}
                                className="text-white text-xl font-bold"
                            >
                                ×
                            </button>

                        </div>

                        <div className="p-6 space-y-5">

                            <div>

                                <label className="block text-gray-700 mb-2 font-medium">
                                    Category Name
                                </label>

                                <input
                                    type="text"
                                    name="category_name"
                                    value={formData.category_name}
                                    onChange={handleChange}
                                    disabled={isEdit}
                                    className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                />

                            </div>

                            <div>

                                <label className="block text-gray-700 mb-2 font-medium">
                                    Type
                                </label>

                                <div className="flex gap-6">

                                    <label className="flex items-center space-x-2">

                                        <input
                                            type="radio"
                                            name="type"
                                            value="Payment"
                                            checked={formData.type==="Payment"}
                                            onChange={handleChange}
                                            disabled={isEdit}
                                            className="accent-green-600"
                                        />

                                        <span>Payment</span>

                                    </label>

                                    <label className="flex items-center space-x-2">

                                        <input
                                            type="radio"
                                            name="type"
                                            value="Receipt"
                                            checked={formData.type==="Receipt"}
                                            onChange={handleChange}
                                            disabled={isEdit}
                                            className="accent-green-600"
                                        />

                                        <span>Receipt</span>

                                    </label>

                                </div>

                            </div>

                            <div>

                                <label className="block text-gray-700 mb-2 font-medium">
                                    Description
                                </label>

                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows="3"
                                    className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                />

                            </div>


                            <div>

                                <label className="block text-gray-700 mb-2 font-medium">
                                    Status
                                </label>

                                <div className="flex gap-6">

                                    <label className="flex items-center space-x-2">

                                        <input
                                            type="radio"
                                            name="status"
                                            value="Active"
                                            checked={formData.status==="Active"}
                                            onChange={handleChange}
                                            className="accent-green-600"
                                        />

                                        <span>Active</span>

                                    </label>

                                    <label className="flex items-center space-x-2">

                                        <input
                                            type="radio"
                                            name="status"
                                            value="Inactive"
                                            checked={formData.status==="Inactive"}
                                            onChange={handleChange}
                                            className="accent-green-600"
                                        />

                                        <span>Inactive</span>

                                    </label>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t">

                                <button
                                    onClick={()=>setShowModal(false)}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                                >
                                    Cancel
                                </button>

                                <button
                                    onClick={handleSave}
                                    className="px-5 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800"
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CashBookCategory;